import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { CalendarRange, ChevronRight, ExternalLink, LucideAngularModule, RefreshCw, Search, SlidersHorizontal } from 'lucide-angular';
import { finalize } from 'rxjs';
import { environment } from '../../environments/environment';
import { AdminApiError } from '../core/admin-api';
import { isValidAdminDateRange, toAdminApiDate } from '../core/admin-date';
import { ActivityStatus, AdminGameListItem } from '../core/models';
import { AdminApiService } from '../data-access/admin-api.service';

@Component({
  selector: 'app-games',
  standalone: true,
  imports: [FormsModule, RouterLink, LucideAngularModule],
  template: `
    <section class="page-heading">
      <div><span class="eyebrow">ACTIVITY OPERATIONS</span><h1>活動管理</h1><p>依活動日期區間查詢未刪除活動，並檢視完整內容。</p></div>
      <div class="summary-pills"><span><b>{{ openCount() }}</b>報名中</span><span><b>{{ endedCount() }}</b>已結束</span></div>
    </section>

    <section class="filter-panel">
      <div class="filter-title"><lucide-angular [img]="SlidersHorizontal" /><span>查詢條件</span><button type="button" (click)="resetFilters()">清除條件</button></div>
      <div class="filter-grid">
        <label class="field search-field"><span>活動名稱或主辦方</span><div><lucide-angular [img]="Search" /><input [(ngModel)]="keyword" placeholder="輸入關鍵字搜尋"></div></label>
        <label class="field"><span>活動區間起日</span><div><lucide-angular [img]="CalendarRange" /><input type="date" [(ngModel)]="startDate"></div></label>
        <label class="field"><span>活動區間迄日</span><div><lucide-angular [img]="CalendarRange" /><input type="date" [(ngModel)]="endDate"></div></label>
        <label class="field"><span>活動狀態</span><select [(ngModel)]="status"><option value="ALL">全部狀態</option><option value="REGISTRATION_OPEN">報名中</option><option value="REGISTRATION_CLOSED">報名截止</option><option value="IN_PROGRESS">活動進行中</option><option value="ENDED">已結束</option></select></label>
      </div>
      <div class="modal-actions"><button class="button primary" [disabled]="loading()" (click)="loadGames()"><lucide-angular [img]="Search" />查詢活動</button></div>
      @if (queryError()) { <p class="form-error">{{ queryError() }}</p> }
    </section>

    <section class="data-card">
      <div class="card-toolbar"><div><h2>活動列表</h2><span>共 {{ filteredGames().length }} 筆結果</span></div>@if (!loading() && !error()) { <span class="sync-note">已依活動日期排序</span> }</div>
      @if (loading()) {
        <div class="empty-state"><span>…</span><h3>正在查詢活動</h3><p>請稍候，正在取得指定日期區間的活動。</p></div>
      } @else if (error()) {
        <div class="empty-state"><span>!</span><h3>活動查詢失敗</h3><p>{{ error() }}</p><button class="button secondary" (click)="loadGames()"><lucide-angular [img]="RefreshCw" />重新查詢</button></div>
      } @else if (filteredGames().length) {
        <div class="desktop-table">
          <table>
            <thead><tr><th>活動</th><th>主辦方</th><th>活動日期</th><th>地點</th><th>狀態</th><th></th></tr></thead>
            <tbody>
              @for (game of filteredGames(); track game.id) {
                <tr>
                  <td><a class="game-cell" [routerLink]="['/games', game.id]"><img [src]="coverImage(game)" [alt]="game.name" (error)="usePlaceholder($event)"><span><strong>{{ game.name }}</strong><small>ID · {{ game.id }}</small></span></a></td>
                  <td><strong class="table-primary">{{ game.companyName || game.owner }}</strong></td>
                  <td><strong class="table-primary">{{ displayDate(game) }}</strong><small>報名至 {{ game.deadline }}</small></td>
                  <td><span class="location-cell"><strong>{{ locationPrimaryLabel(game) }}</strong>@if (game.location.address) { <small>{{ game.location.address }}</small> }</span></td>
                  <td><span class="status-badge" [attr.data-status]="game.activityStatus">{{ statusLabel(game.activityStatus) }}</span></td>
                  <td><a class="row-action" [href]="frontendUrl(game)" target="_blank" rel="noopener" aria-label="前台活動"><lucide-angular [img]="ExternalLink" /></a><a class="row-action" [routerLink]="['/games', game.id]" aria-label="查看活動"><lucide-angular [img]="ChevronRight" /></a></td>
                </tr>
              }
            </tbody>
          </table>
        </div>
        <div class="mobile-list">
          @for (game of filteredGames(); track game.id) {
            <article class="mobile-game">
              <a class="mobile-game-image" [routerLink]="['/games', game.id]"><img [src]="coverImage(game)" [alt]="game.name" (error)="usePlaceholder($event)"></a><div><span class="status-badge" [attr.data-status]="game.activityStatus">{{ statusLabel(game.activityStatus) }}</span><h3><a [routerLink]="['/games', game.id]">{{ game.name }}</a></h3><p>{{ game.companyName || game.owner }}</p><small>{{ displayDate(game) }}</small><span class="mobile-location"><span>{{ locationPrimaryLabel(game) }}</span>@if (game.location.address) { <small>{{ game.location.address }}</small> }</span><a [href]="frontendUrl(game)" target="_blank" rel="noopener">查看前台活動</a></div><a [routerLink]="['/games', game.id]" aria-label="查看活動"><lucide-angular [img]="ChevronRight" /></a>
            </article>
          }
        </div>
      } @else {
        <div class="empty-state"><span>0</span><h3>找不到符合條件的活動</h3><p>請調整活動日期、狀態或關鍵字後再試一次。</p><button class="button secondary" (click)="resetFilters()">清除前端篩選</button></div>
      }
    </section>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GamesComponent implements OnInit {
  private readonly api = inject(AdminApiService);
  private readonly games = signal<AdminGameListItem[]>([]);
  readonly loading = signal(false);
  readonly error = signal('');
  readonly queryError = signal('');
  keyword = '';
  startDate = this.defaultStartDate();
  endDate = this.defaultEndDate();
  status: ActivityStatus | 'ALL' = 'ALL';
  readonly Search = Search;
  readonly CalendarRange = CalendarRange;
  readonly SlidersHorizontal = SlidersHorizontal;
  readonly ChevronRight = ChevronRight;
  readonly ExternalLink = ExternalLink;
  readonly RefreshCw = RefreshCw;

  readonly openCount = computed(() => this.games().filter(game => game.activityStatus === 'REGISTRATION_OPEN').length);
  readonly endedCount = computed(() => this.games().filter(game => game.activityStatus === 'ENDED').length);
  filteredGames(): AdminGameListItem[] {
    const keyword = this.keyword.trim().toLowerCase();
    return this.games().filter(game => {
      if (this.status !== 'ALL' && game.activityStatus !== this.status) return false;
      return !keyword || `${game.name} ${game.companyName ?? ''} ${game.owner}`.toLowerCase().includes(keyword);
    });
  }

  ngOnInit(): void { this.loadGames(); }

  loadGames(): void {
    if (this.loading()) return;
    if (!isValidAdminDateRange(this.startDate, this.endDate)) {
      this.queryError.set('請輸入有效的活動日期區間，且起日不可晚於迄日。');
      return;
    }
    this.queryError.set('');
    this.error.set('');
    this.loading.set(true);
    this.api.getActiveGames(toAdminApiDate(this.startDate), toAdminApiDate(this.endDate)).pipe(
      finalize(() => this.loading.set(false)),
    ).subscribe({
      next: games => this.games.set([...games].sort((a, b) => a.gameStartDate.localeCompare(b.gameStartDate) || a.name.localeCompare(b.name, 'zh-TW'))),
      error: (error: AdminApiError) => this.error.set(error.message),
    });
  }

  resetFilters(): void { this.keyword = ''; this.status = 'ALL'; }
  statusLabel(status: ActivityStatus): string { return { REGISTRATION_OPEN: '報名中', REGISTRATION_CLOSED: '報名截止', IN_PROGRESS: '進行中', ENDED: '已結束' }[status]; }
  displayDate(game: AdminGameListItem): string { return game.gameStartDate === game.gameEndDate ? game.gameStartDate : `${game.gameStartDate} - ${game.gameEndDate}`; }
  locationPrimaryLabel(game: AdminGameListItem): string { return [game.location?.area, game.location?.venue].filter(Boolean).join(' · ') || '未提供'; }
  coverImage(game: AdminGameListItem): string {
    const image = game.image?.find(item => item.category === 'main' && item.imageId);
    return image ? `${environment.imagesUrl}/games/${game.id}/images/${image.imageId}` : this.placeholder;
  }
  frontendUrl(game: AdminGameListItem): string { return `${environment.frontendUrl}/detail/${game.id}`; }
  usePlaceholder(event: Event): void { (event.target as HTMLImageElement).src = this.placeholder; }

  private readonly placeholder = 'assets/logos/crewjam/cj.svg';
  private defaultStartDate(): string { const date = new Date(); date.setDate(1); return this.inputDate(date); }
  private defaultEndDate(): string { const date = new Date(); date.setMonth(date.getMonth() + 2, 0); return this.inputDate(date); }
  private inputDate(date: Date): string { return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`; }
}
